import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, Box, Typography
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import TimeSlotSelector from '../../../../../../components/appointments/TimeSlotSelector';
import { useNotification } from '../../../../../../context/NotificationContext';
import { getStorefrontCompanyById, rescheduleStorefrontAppointment } from '../../../../../../api/storefront/client';

const formatDateTime = (dateTime) => {
  if (!dateTime) return 'No date available';
  try {
    const date = dateTime instanceof Date
      ? dateTime
      : dateTime?.toDate && typeof dateTime.toDate === 'function'
        ? dateTime.toDate()
        : new Date(dateTime);
    return format(date, 'EEEE, MMMM d, yyyy • h:mm a');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'No date available';
  }
};

const formatCompanyHours = (hours) => {
  if (!hours || typeof hours !== 'object') return {};
  const formattedHours = {};
  Object.entries(hours).forEach(([day, dayHours]) => {
    formattedHours[day] = {
      ...dayHours,
      open: dayHours?.open ? String(dayHours.open).toUpperCase() : null,
      close: dayHours?.close ? String(dayHours.close).toUpperCase() : null,
      breakStart: dayHours?.breakStart ? String(dayHours.breakStart).toUpperCase() : null,
      breakEnd: dayHours?.breakEnd ? String(dayHours.breakEnd).toUpperCase() : null,
    };
  });
  return formattedHours;
};

const RescheduleDialog = ({ 
  open, 
  onClose, 
  appointment, 
  onReschedule,
  onError
}) => {
  const [companyHours, setCompanyHours] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const { setNotification } = useNotification();

  const companyId = useMemo(() => {
    return appointment?.companyId || localStorage.getItem('clientCompanyId');
  }, [appointment?.companyId]);

  const serviceForSlots = useMemo(() => ({
    id: appointment?.serviceId,
    duration: appointment?.duration,
    ...(appointment?.serviceTags?.[0] || {})
  }), [appointment]);

  const fetchCompanyHours = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      const company = await getStorefrontCompanyById(companyId);
      const hours = company?.settings?.companyHours;
      if (!hours) return;
      setCompanyHours(formatCompanyHours(hours));
    } catch (e) {
      console.error('Error fetching company hours:', e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (open) {
      setSelectedDate(null);
      setSelectedDateTime(null);
      fetchCompanyHours();
    }
  }, [open, fetchCompanyHours]);

  const handleTimeSelect = useCallback((slot) => {
    if (!slot) return;
    if (slot.dateTime) {
      requestAnimationFrame(() => {
        setSelectedDateTime(slot.dateTime);
      });
    }
  }, []);

  const handleReschedule = useCallback(async () => {
    if (!companyId || !appointment?.id || !selectedDateTime) return;
    try {
      await rescheduleStorefrontAppointment({
        companyId,
        appointmentId: appointment.id,
        dateTime: selectedDateTime,
      });

      // Notify client of success
      setNotification({
        open: true,
        message: 'Appointment rescheduled successfully! You\'ll receive a confirmation email shortly.',
        severity: 'success',
        autoHideDuration: 3000
      });

      if (onReschedule) onReschedule(selectedDateTime);
    } catch (e) {
      console.error('Error rescheduling appointment:', e);
      setNotification({
        open: true,
        message: 'Failed to reschedule appointment',
        severity: 'error',
        autoHideDuration: 3000
      });
      onError?.(e.message || 'Failed to reschedule');
    }
  }, [companyId, appointment, selectedDateTime, onReschedule, onError]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogTitle>
        Reschedule Appointment
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ bgcolor: 'primary.lighter', p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" color="primary.main">
              Current Time
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDateTime(appointment?.dateTime)}
            </Typography>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
              <DatePicker
                label="Select New Date"
                value={selectedDate}
                onChange={(newValue) => {
                  if (newValue && !isNaN(newValue.getTime())) {
                    setSelectedDate(newValue);
                    setSelectedDateTime(null);
                  }
                }}
                disablePast
                slotProps={{
                  textField: { fullWidth: true, size: 'medium', variant: 'outlined' }
                }}
              />

              {selectedDate && (
                <Box sx={{ 
                  bgcolor: 'background.paper', 
                  borderRadius: 2, 
                  border: '1px solid', 
                  borderColor: 'divider', 
                  p: 2 
                }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Available Times
                  </Typography>
                  <TimeSlotSelector
                    key={selectedDate.toISOString()}
                    selectedDate={selectedDate}
                    companyHours={companyHours}
                    onTimeSelect={handleTimeSelect}
                    companyId={companyId}
                    selectedTime={selectedDateTime}
                    selectedService={serviceForSlots}
                    excludeAppointmentId={appointment?.id}
                  />
                </Box>
              )}

              {selectedDateTime && (
                <Box sx={{ bgcolor: 'success.lighter', p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" color="success.main" gutterBottom>
                    New Appointment Time
                  </Typography>
                  <Typography variant="body2" color="success.dark">
                    {formatDateTime(selectedDateTime)}
                  </Typography>
                </Box>
              )}
            </Box>
          </LocalizationProvider>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleReschedule}
          variant="contained" 
          color="primary" 
          disabled={!selectedDateTime}
        >
          Reschedule
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RescheduleDialog;


